import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { isPdfServerEnabled } from '../../lib/health';

export type HtmlExportOptions = {
  /** Embarquer les captures 3D (vidéos turntable + images) en base64 — interactif, fichier plus lourd. */
  include3D: boolean;
  /**
   * Mode du viewer 3D dans l'export HTML :
   *  - 'none'        : pas de maquette 3D (léger).
   *  - 'static'      : embarque les captures existantes du projet comme gallerie 6 vues.
   *  - 'interactive' : inclut un loader qui fetche l'IFC depuis R2 (lien expirant) et le rend.
   */
  viewer_mode: 'none' | 'static' | 'interactive';
  /** Durée de vie du lien IFC signé (jours) en mode interactif. */
  viewer_link_expires_days?: number;
  /** Id du document IFC à partager (mode interactif). Requis si plusieurs IFC dans le projet. */
  ifc_document_id?: string;
};

type IfcModel = {
  id: string;
  name: string;
  title: string;
  size: number;
  uploaded_at: string;
  discipline: string | null;
};

type Props = {
  projectId: number;
  title: string;
  kind: 'pilotage' | 'presentation' | 'composition';
  payload: any;
  onClose: () => void;
  /** Si fourni, le bouton PDF appelle ce callback (l'appelant ferme la modale puis capture le DOM). */
  onPdf?: () => Promise<void> | void;
  /** Si fourni, exporte le DOM réellement rendu (SVG + images inlinées) plutôt qu'une reconstruction. */
  onHtml?: (opts: HtmlExportOptions) => Promise<void> | void;
};

export default function ExportModal({ projectId, title, kind, payload, onClose, onPdf, onHtml }: Props) {
  const [expDays, setExpDays] = useState<number>(30);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [include3D, setInclude3D] = useState<boolean>(true);
  const [viewerMode, setViewerMode] = useState<'none' | 'static' | 'interactive'>('static');
  const [viewerLinkDays, setViewerLinkDays] = useState<number>(30);
  // Liste des IFC du projet — chargée au montage. null = en cours, [] = aucun, array = OK.
  const [ifcModels, setIfcModels] = useState<IfcModel[] | null>(null);
  const [ifcDocId, setIfcDocId] = useState<string>('');
  const [ifcLoadError, setIfcLoadError] = useState<string | null>(null);
  // Flag Browser Rendering — désactivé en prod gratuit.
  const [pdfServerOn, setPdfServerOn] = useState<boolean>(false);
  useEffect(() => {
    isPdfServerEnabled().then(setPdfServerOn);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ ifcs: IfcModel[] }>(`/projects/${projectId}/ifc-models`)
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r?.ifcs) ? r.ifcs : [];
        setIfcModels(list);
        setIfcLoadError(null);
        if (list.length === 1) setIfcDocId(list[0].id);
      })
      .catch((e) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn('[ExportModal] ifc-models fetch failed', e);
        setIfcModels([]);
        setIfcLoadError(String(e?.message ?? e));
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const ifcCount = ifcModels?.length ?? 0;
  const interactiveDisabled = ifcModels !== null && ifcCount === 0;
  // Si l'utilisateur avait sélectionné Interactif mais qu'on apprend qu'il n'y a pas d'IFC,
  // on bascule silencieusement sur Statique pour éviter un export cassé.
  useEffect(() => {
    if (interactiveDisabled && viewerMode === 'interactive') setViewerMode('static');
  }, [interactiveDisabled, viewerMode]);

  async function handlePdf() {
    if (onPdf) {
      try {
        await onPdf();
      } catch (e: any) {
        alert(`Export PDF impossible : ${e?.message ?? e}`);
      }
      return;
    }
    // Fallback : impression simple
    window.print();
  }

  /** Export PDF propre via Cloudflare Browser Rendering (pas de headers Chrome).
   *  Accepté pour `presentation` ET `composition` : même pipeline de rendu côté serveur. */
  async function handleServerPdf() {
    if (kind === 'pilotage') {
      alert('Export PDF serveur non disponible pour le Pilotage. Utilise "Imprimer via le navigateur".');
      return;
    }
    setBusy(true);
    try {
      // Fetch direct : l'endpoint renvoie un blob PDF, pas du JSON
      // On lit l'orientation choisie par l'utilisateur dans le payload de la présentation.
      // Si le masque custom la verrouille, c'est celle-là. Sinon défaut portrait.
      const presentation = (payload as any)?.presentation ?? {};
      const orientation: 'portrait' | 'landscape' =
        presentation?.custom?.mask_orientation ??
        (presentation?.orientation === 'landscape' ? 'landscape' : 'portrait');
      const res = await fetch(`/api/v1/projects/${projectId}/synthesis/export-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload,
          title,
          orientation,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Export PDF serveur impossible : ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleHtmlDomCapture() {
    // Garde-fou : si mode interactif sans IFC sélectionné mais plusieurs dispos,
    // on bloque pour forcer le choix.
    if (viewerMode === 'interactive' && ifcCount > 1 && !ifcDocId) {
      alert('Choisis quel modèle IFC partager avant d\'exporter.');
      return;
    }
    if (onHtml) {
      setBusy(true);
      try {
        await onHtml({
          include3D,
          viewer_mode: viewerMode,
          viewer_link_expires_days: viewerLinkDays,
          ifc_document_id: viewerMode === 'interactive' ? ifcDocId || undefined : undefined,
        });
      } catch (e: any) {
        alert(`Export HTML impossible : ${e?.message ?? e}`);
      } finally {
        setBusy(false);
      }
      return;
    }
    handleHtml();
  }

  function handleHtml() {
    // Génère un HTML autonome. Si la présentation a un thème, on récupère les styles
    // actifs dans le document pour les embarquer.
    const json = JSON.stringify(payload);
    // Extrait les styles globaux pertinents (les règles .pres-theme-*, .presentation-slide, etc.)
    let themeCss = '';
    try {
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          const rules = (sheet as CSSStyleSheet).cssRules;
          if (!rules) continue;
          for (const rule of Array.from(rules)) {
            const txt = rule.cssText;
            if (
              /pres-theme|presentation-slide|slide-hero|slide-kpi|slide-capture|slide-table|slide-timeline|chart-tooltip|count-anim|stat-card/.test(
                txt
              )
            ) {
              themeCss += txt + '\n';
            }
          }
        } catch {
          /* cross-origin */
        }
      }
    } catch {
      /* */
    }
    const html = `<!doctype html>
<html lang="fr"><head>
<meta charset="utf-8"/>
<title>${escapeHtml(title)} — Synthèse BIM</title>
<style>
  :root{--c-sage:#81B29A;--c-terracotta:#E07A5F;--c-bg:#FAFAF5;--c-text:#3D5A45;--c-muted:#A9A193;--c-border:#DDD8CE;--c-nav:#F0EDE5;}
  body{font-family:-apple-system,Segoe UI,sans-serif;background:var(--c-bg);color:var(--c-text);margin:0;padding:24px;}
  h1{font-size:22px;margin:0 0 8px 0}
  .card{background:#fff;border:1px solid var(--c-border);border-radius:12px;padding:16px;margin-bottom:14px;box-shadow:0 1px 3px rgba(0,0,0,0.04)}
  .kpis,.slide-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px}
  .kpi,.slide-kpi{background:var(--c-nav);border-radius:10px;padding:12px;border-left:4px solid var(--c-sage)}
  .kpi .v,.slide-kpi .kpi-val{font-size:26px;font-weight:700}
  .kpi .l,.slide-kpi .kpi-lbl{font-size:11px;color:var(--c-muted);text-transform:uppercase;letter-spacing:0.05em}
  pre{white-space:pre-wrap;background:var(--c-nav);padding:12px;border-radius:8px;font-size:11px}
  .foot{font-size:11px;color:var(--c-muted);margin-top:24px;text-align:center}
  .presentation-slides{display:flex;flex-wrap:wrap;gap:16px;align-items:stretch;align-content:flex-start}
  .presentation-slide{flex:1 1 100%;min-width:0;background:#fff;border:1px solid var(--c-border);border-radius:14px;padding:32px;box-shadow:0 1px 2px rgba(0,0,0,0.04);box-sizing:border-box}
  .presentation-slide.slide-w-full{flex-basis:100%}
  .presentation-slide.slide-w-half{flex-basis:calc(50% - 8px)}
  .presentation-slide.slide-w-third{flex-basis:calc(33.3333% - 10.666px)}
  .presentation-slide.slide-hero{flex-basis:100%!important}
  @media print { *,*::before,*::after{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    @page{size:A4;margin:10mm 10mm 14mm 10mm}
    .presentation-slide{page-break-inside:avoid!important;break-inside:avoid!important;box-shadow:none!important}
    .presentation-slide.slide-hero{page-break-after:always!important;break-after:page!important}
  }
  .slide-hero{background:linear-gradient(135deg,var(--c-sage),var(--c-text));color:#fff;padding:56px 44px;border:none}
  .slide-hero h1{font-size:38px;margin:0 0 10px 0}
  .slide-timeline{position:relative;padding-left:24px;border-left:2px solid var(--c-sage)}
  .slide-timeline .tl-item{position:relative;padding:4px 0 16px 10px}
  .slide-timeline .tl-item::before{content:'';position:absolute;left:-30px;top:8px;width:14px;height:14px;border-radius:50%;background:var(--c-sage);border:3px solid #fff;box-shadow:0 0 0 2px var(--c-sage)}
  .slide-timeline .tl-date{font-weight:600;font-size:12px;color:var(--c-muted)}
  .slide-timeline .tl-title{font-weight:600;margin:2px 0 4px 0}
  .slide-table{width:100%;border-collapse:collapse}
  .slide-table th,.slide-table td{padding:8px 12px;border-bottom:1px solid var(--c-border);text-align:left;font-size:13px}
  .slide-table th{background:var(--c-nav);font-weight:600}
  ${themeCss}
</style></head><body>
<h1>${escapeHtml(title)}</h1>
<div class="card-meta">Exporté le ${new Date().toLocaleString('fr-CH')}</div>
<div id="app"></div>
<div class="foot">Synthèse générée par Portail BIM — Laurent Seidler</div>
<script>
  const DATA = ${json};
  const app = document.getElementById('app');
  function escapeHtml(s){return String(s).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))}
  if (DATA && DATA.metrics) {
    const s = DATA.metrics.stats || {};
    const kpis = document.createElement('div'); kpis.className='card';
    kpis.innerHTML = '<h2 style="margin:0 0 12px 0">Chiffres clés</h2><div class="kpis">' +
      Object.entries(s).map(([k,v])=>'<div class="kpi"><div class="l">'+escapeHtml(k.replace(/_/g," "))+'</div><div class="v">'+escapeHtml(String(v))+'</div></div>').join('')+
      '</div>';
    app.appendChild(kpis);
    if (DATA.narrative) {
      const n = document.createElement('div'); n.className='card';
      n.innerHTML = '<h2 style="margin:0 0 12px 0">Récit</h2><div style="white-space:pre-wrap;line-height:1.7">'+escapeHtml(DATA.narrative)+'</div>';
      app.appendChild(n);
    }
  } else if (DATA && DATA.presentation) {
    const p = DATA.presentation;
    const themeId = p.theme || 'corporate';
    const wrap = document.createElement('div');
    wrap.className = 'pres-theme-wrap pres-theme pres-theme-' + themeId;
    const slides = document.createElement('div'); slides.className = 'presentation-slides';
    wrap.appendChild(slides);
    (p.sections||[]).forEach((sec, i)=>{
      const d = document.createElement('div');
      const w = sec.width || 'full';
      d.className = 'presentation-slide slide-w-' + w + (sec.type==='hero'?' slide-hero':'');
      d.setAttribute('data-section-idx', String(i+1).padStart(2, '0'));
      let html = '';
      if (sec.type === 'hero') {
        html += '<h1>'+escapeHtml(sec.title||p.title||'')+'</h1>';
        if (sec.body || p.subtitle) html += '<p>'+escapeHtml(sec.body||p.subtitle||'')+'</p>';
      } else {
        if (sec.title) html += '<h2>'+escapeHtml(sec.title)+'</h2>';
        if (sec.body) html += '<div style="white-space:pre-wrap;line-height:1.7">'+escapeHtml(sec.body)+'</div>';
      }
      if (sec.kpis) html += '<div class="slide-kpis">'+sec.kpis.map(k=>'<div class="slide-kpi"><div class="kpi-lbl">'+escapeHtml(k.label)+'</div><div class="kpi-val">'+escapeHtml(String(k.value))+'</div>'+(k.trend?'<div class="kpi-trend">'+escapeHtml(k.trend)+'</div>':'')+'</div>').join('')+'</div>';
      if (sec.quote) html += '<blockquote>'+escapeHtml(sec.quote)+'</blockquote>';
      if (sec.table) {
        html += '<table class="slide-table"><thead><tr>'+(sec.table.columns||[]).map(c=>'<th>'+escapeHtml(c)+'</th>').join('')+'</tr></thead><tbody>'+(sec.table.rows||[]).map(r=>'<tr>'+r.map(c=>'<td>'+escapeHtml(String(c))+'</td>').join('')+'</tr>').join('')+'</tbody></table>';
      }
      if (sec.timeline && sec.timeline.items) {
        html += '<div class="slide-timeline">'+sec.timeline.items.map(it=>'<div class="tl-item"><div class="tl-date">'+escapeHtml(it.date||'')+'</div><div class="tl-title">'+escapeHtml(it.title||'')+'</div>'+(it.text?'<div>'+escapeHtml(it.text)+'</div>':'')+'</div>').join('')+'</div>';
      }
      d.innerHTML = html;
      slides.appendChild(d);
    });
    app.appendChild(wrap);
  }
</script></body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `synthese-${title.toLowerCase().replace(/\W+/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleShare() {
    setBusy(true);
    try {
      const r = await api.post<{ token: string; expires_at: string | null }>(
        `/projects/${projectId}/synthesis/share`,
        {
          kind,
          title,
          payload,
          expires_days: expDays,
        }
      );
      const url = `${window.location.origin}/share/${r.token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* noop */
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="export-modal" onClick={onClose}>
      <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <strong>Exporter « {title} »</strong>
          <button className="btn btn-ghost" onClick={onClose}>
            ×
          </button>
        </div>

        {pdfServerOn && (kind === 'presentation' || kind === 'composition') && (
          <button className="export-choice" onClick={handleServerPdf} disabled={busy}>
            <span className="ic">PDF</span>
            <div>
              <div className="ttl">PDF propre (rendu serveur) — recommandé</div>
              <div className="sub">
                {busy
                  ? 'Génération serveur en cours (~10-20 s)…'
                  : 'Cloudflare Browser Rendering. A4 portrait, header/footer custom (titre + pagination), pas d\'artefacts Chrome. Nécessite plan Workers Paid activé.'}
              </div>
            </div>
          </button>
        )}
        <button className="export-choice" onClick={handlePdf}>
          <span className="ic">🖨</span>
          <div>
            <div className="ttl">Imprimer via le navigateur (fallback)</div>
            <div className="sub">
              Ouvre la dialog d'impression → choisis « Enregistrer en PDF ». <b>Décoche « En-têtes et pieds de page »</b> dans « Plus de paramètres » pour supprimer la date/URL ajoutées par Chrome.
            </div>
          </div>
        </button>
        <div className="export-choice" style={{ display: 'block', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="ic">🌐</span>
            <div style={{ flex: 1 }}>
              <div className="ttl">HTML interactif autonome</div>
              <div className="sub">
                Fichier unique : graphiques SVG, captures en base64, CSS embarqué. Ouvrable hors-ligne.
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 10, fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={include3D}
              onChange={(e) => setInclude3D(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              <b>Embarquer les captures 3D et vidéos turntable</b> (fichier plus lourd, ~5–30 Mo).
              Les vidéos turntable gardent leurs contrôles (pause, seek, replay) pour une exploration 360°
              dans le fichier exporté. Décocher pour un export ultra-léger sans médias.
            </span>
          </label>

          {/* Mode maquette 3D */}
          <div style={{ marginTop: 14, fontSize: 11 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Maquette 3D dans l'export</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name="viewer-mode" checked={viewerMode === 'none'} onChange={() => setViewerMode('none')} />
                <span><b>Aucune</b> — fichier très léger, pas de 3D.</span>
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name="viewer-mode" checked={viewerMode === 'static'} onChange={() => setViewerMode('static')} />
                <span>
                  <b>Statique (offline)</b> — galerie des captures précalculées du projet. Fonctionne sans
                  internet. ~10 Mo si captures embarquées. À utiliser pour un archivage long terme.
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'flex-start',
                  cursor: interactiveDisabled ? 'not-allowed' : 'pointer',
                  opacity: interactiveDisabled ? 0.55 : 1,
                }}
              >
                <input
                  type="radio"
                  name="viewer-mode"
                  checked={viewerMode === 'interactive'}
                  onChange={() => setViewerMode('interactive')}
                  disabled={interactiveDisabled}
                />
                <span>
                  <b>Interactif (online)</b> — viewer IFC embarqué, fichier IFC servi depuis R2 via un lien
                  expirant. Le destinataire a besoin d'internet pour charger la maquette.
                  {ifcModels === null && (
                    <span style={{ color: 'var(--c-muted)', display: 'block', marginTop: 2 }}>
                      Détection des fichiers IFC du projet…
                    </span>
                  )}
                  {interactiveDisabled && (
                    <span style={{ color: 'var(--c-muted)', display: 'block', marginTop: 2 }}>
                      Aucun fichier IFC dans ce projet. Déposez un fichier .ifc dans l'onglet Documents
                      pour activer cette option.
                    </span>
                  )}
                  {ifcModels && ifcCount === 1 && (
                    <span style={{ color: 'var(--c-muted)', display: 'block', marginTop: 2 }}>
                      Maquette à partager : <b>{ifcModels[0].name}</b>
                      {ifcModels[0].discipline ? ` (${ifcModels[0].discipline})` : ''}
                    </span>
                  )}
                </span>
              </label>
            </div>

            {/* Mode interactif + plusieurs IFC → sélecteur obligatoire */}
            {viewerMode === 'interactive' && ifcModels && ifcCount > 1 && (
              <div style={{ marginTop: 10, background: 'var(--c-nav)', padding: 8, borderRadius: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Quel modèle partager ?
                </label>
                <select
                  value={ifcDocId}
                  onChange={(e) => setIfcDocId(e.target.value)}
                  style={{ width: '100%', padding: 6, fontSize: 12 }}
                >
                  <option value="">— Sélectionner —</option>
                  {ifcModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.discipline ? ` · ${m.discipline}` : ''}
                      {m.size > 0 ? ` · ${formatBytes(m.size)}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {viewerMode === 'interactive' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                <label>Lien IFC expire dans</label>
                <select value={viewerLinkDays} onChange={(e) => setViewerLinkDays(Number(e.target.value))} style={{ padding: 4 }}>
                  <option value={7}>7 jours</option>
                  <option value={30}>30 jours</option>
                  <option value={90}>90 jours</option>
                </select>
              </div>
            )}
            {ifcLoadError && (
              <div style={{ marginTop: 6, fontSize: 10, color: 'var(--c-muted)' }}>
                Impossible de récupérer la liste des IFC : {ifcLoadError}
              </div>
            )}
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: 10, width: '100%' }}
            onClick={handleHtmlDomCapture}
            disabled={busy}
          >
            {busy ? 'Export en cours…' : 'Télécharger le fichier HTML'}
          </button>
        </div>

        <div
          className="export-choice"
          style={{ display: 'block', cursor: 'default' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="ic">🔗</span>
            <div style={{ flex: 1 }}>
              <div className="ttl">Lien partageable (lecture seule)</div>
              <div className="sub">Le destinataire ouvre dans son navigateur sans compte</div>
            </div>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12 }}>Expire dans</label>
            <select
              value={expDays}
              onChange={(e) => setExpDays(Number(e.target.value))}
              style={{ padding: 4 }}
            >
              <option value={1}>1 jour</option>
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
              <option value={365}>1 an</option>
            </select>
            <button className="btn btn-primary" onClick={handleShare} disabled={busy}>
              {busy ? '…' : 'Générer'}
            </button>
          </div>
          {shareUrl && (
            <div style={{ marginTop: 10, fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}>✅ Lien copié dans le presse-papiers :</div>
              <input
                type="text"
                value={shareUrl}
                readOnly
                style={{ width: '100%', padding: 6, fontSize: 11 }}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!)
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}
